import React, { useState, useEffect } from 'react';
import { corporatesApi } from '../../services/api';
import { useAuth } from '../context/AuthContext';
import {
    Building2,
    Plus,
    Pencil,
    Trash2,
    X,
    Search,
    Phone,
    MapPin,
    AlertCircle,
    Eye
} from 'lucide-react';

const CorporatesPage = () => {
    const { user } = useAuth();
    const [corporates, setCorporates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCorporate, setEditingCorporate] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        city: '',
        district: '',
        address: '',
        phoneNumber: '',
        status: 1
    });
    const [saving, setSaving] = useState(false);
    const [viewOnly, setViewOnly] = useState(false);

    useEffect(() => {
        loadCorporates();
    }, []);

    const loadCorporates = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await corporatesApi.getAll();
            setCorporates(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingCorporate(null);
        setFormData({
            name: '',
            type: '',
            city: '',
            district: '',
            address: '',
            phoneNumber: '',
            status: 1
        });
        setShowModal(true);
    };

    const handleView = (corporate) => {
        setViewOnly(true);
        setEditingCorporate(corporate);
        setFormData({
            name: corporate.name || '',
            type: corporate.type || '',
            city: corporate.city || '',
            district: corporate.district || '',
            address: corporate.address || '',
            phoneNumber: corporate.phoneNumber || '',
            status: corporate.status
        });
        setShowModal(true);
    };

    const handleEdit = (corporate) => {
        setViewOnly(false);
        setEditingCorporate(corporate);
        setFormData({
            name: corporate.name || '',
            type: corporate.type || '',
            city: corporate.city || '',
            district: corporate.district || '',
            address: corporate.address || '',
            phoneNumber: corporate.phoneNumber || '',
            status: corporate.status
        });
        setShowModal(true);
    };

    const handleDelete = async (corporate) => {
        if (!window.confirm(`"${corporate.name}" kurumunu silmek istediğinizden emin misiniz?`)) {
            return;
        }

        try {
            await corporatesApi.delete(corporate.id);
            corporate.status = 0;
            setCorporates([...corporates]);
        } catch (err) {
            alert('Silme işlemi başarısız: ' + err.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (editingCorporate) {
                const updated = await corporatesApi.update(editingCorporate.id, formData);
                setCorporates(corporates.map(c => c.id === editingCorporate.id ? updated : c));
            } else {
                const created = await corporatesApi.create(formData);
                setCorporates([...corporates, created]);
            }
            setShowModal(false);
        } catch (err) {
            alert('Kaydetme işlemi başarısız: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const getStatusBadge = (status) => {
        return status === 1 ? (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                Aktif
            </span>
        ) : (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                Pasif
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const filteredCorporates = corporates.filter(corporate =>
        corporate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        corporate.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        corporate.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Kurumlar</h1>
                        <p className="text-gray-500 mt-1">Tüm kurumları yönetin</p>
                    </div>
                    {user?.roleId === 0 && (
                        <button
                            onClick={handleAdd}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-5 h-5" />
                            Yeni Kurum
                        </button>
                    )}
                </div>

                {/* Search */}
                <div className="bg-white rounded-xl shadow-sm border p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Kurum adı, şehir veya tür ile ara..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Corporates Grid */}
                {filteredCorporates.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
                        <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p>Kurum bulunamadı.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCorporates.map((corporate) => (
                            <div
                                key={corporate.id}
                                className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
                            >
                                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <Building2 className="w-8 h-8 text-white" />
                                        {getStatusBadge(corporate.status)}
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mt-3">{corporate.name}</h3>
                                    <p className="text-purple-100 text-sm">{corporate.type}</p>
                                </div>

                                <div className="p-6 space-y-3">
                                    {(corporate.city || corporate.district) && (
                                        <div className="flex items-start gap-3 text-sm">
                                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                                            <span className="text-gray-600">
                                                {[corporate.district, corporate.city].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    {corporate.phoneNumber && (
                                        <div className="flex items-center gap-3 text-sm">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span className="text-gray-600">{corporate.phoneNumber}</span>
                                        </div>
                                    )}

                                    <div className="pt-3 border-t flex items-center justify-between">
                                        <div className="text-sm text-gray-500">
                                            <span className="font-medium text-gray-700">{corporate.locationCount || 0}</span> lokasyon
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {user?.roleId === 0 ? (
                                                <button
                                                    onClick={() => handleEdit(corporate)}
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                    title="Düzenle"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleView(corporate)}
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                    title="Görüntüle"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {viewOnly ? 'Kurum Bilgileri' : editingCorporate ? 'Kurum Düzenle' : 'Yeni Kurum'}
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Kurum Adı *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                    placeholder="Örn: Şehir Hastanesi"
                                    required
                                    disabled={viewOnly}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Durum
                                </label>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 1}
                                            onChange={() => setFormData({ ...formData, status: 1 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            disabled={viewOnly}
                                        />
                                        <span className="text-sm text-gray-700">Aktif</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 0}
                                            onChange={() => setFormData({ ...formData, status: 0 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            disabled={viewOnly}
                                        />
                                        <span className="text-sm text-gray-700">Pasif</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tür
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                    disabled={viewOnly}
                                >
                                    <option value="">Seçin</option>
                                    <option value="Devlet Hastanesi">Devlet Hastanesi</option>
                                    <option value="Özel Hastane">Özel Hastane</option>
                                    <option value="Üniversite Hastanesi">Üniversite Hastanesi</option>
                                    <option value="Klinik">Klinik</option>
                                    <option value="Sağlık Merkezi">Sağlık Merkezi</option>
                                    <option value="Diğer">Diğer</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Şehir
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                        placeholder="Örn: İstanbul"
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        İlçe
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.district}
                                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                        placeholder="Örn: Kadıköy"
                                        disabled={viewOnly}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Adres
                                </label>
                                <textarea
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                    rows={3}
                                    placeholder="Tam adres..."
                                    disabled={viewOnly}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Telefon
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                                    placeholder="Örn: 0212 123 45 67"
                                    disabled={viewOnly}
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    {viewOnly ? 'Kapat' : 'İptal'}
                                </button>
                                {!viewOnly && (
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                    >
                                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CorporatesPage;
